import React, { useEffect, useState } from "react";
import Section from "../../components/Section";
import { pageTitle } from "../../helpers/PageTitle";
import axiosInstance from "../../utils/axiosInstance";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchAcceptedPendingAppointmentsOfDoctorByDate, fetchAppointmentDetails, fetchDoctorProfile, fetchMyUserDetails, fetchPatients, imageBaseUrl } from "../../constants/ApiConstants";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { linearTopGradient, tuftsBlue } from "../../constants/colors";
import toast from "react-hot-toast";
import FullPageLoader from "../../components/FullPageLoader";
import PageHeader from "../../components/PageHeader";
import LoadMore from "../../components/LoadMore";
import MyModal from "../../components/MyModal";
import InputField from "../../components/InputField";
import DoctorDetailHeader from "../../components/DoctorDetailHeader";
import BookingDateSelector from "../AppointmentDateSelect/comps/BookingDateSelector";
import Spinner from "../../components/Spinner";
import ListEmptyText from "../../components/ListEmptyText";
import { format4DigitTimeToAmOrPM, getAgeFromGivenDate } from "../../utils/utils";
import CommonMobileHeader from "../../components/Header/CommonMobileHeader";

export default function DoctorDashboard() {
  pageTitle("Dashboard - Doctor");

  const dateLimit = 90;
  const getBookingDates = () =>
    Array.from({ length: dateLimit }, (_, i) => {
      const now = new Date();
      const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      return nextDay;
    });

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.appSlice.doctorData?.id);
  const [doctorDetail, setDoctorDetail] = useState(null);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [count, setCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingDates, setBookingDates] = useState(getBookingDates());
  const [appointmentDataFetching, setAppointmentByDateFetching] = useState(false);
  const [bookedAppointments, setBookedAppointments] = useState();

  const rowLimit = 10;

  useEffect(() => {
    fetchDoctorDetail();
    setSelectedDate(bookingDates[0]);
    fetchAcceptedPendingAppointmentsOfDoctor();
  }, []);

  useEffect(() => {
    if (doctorDetail && selectedDate) {
      fetchAcceptedPendingAppointmentsOfDoctor();
    }
  }, [selectedDate]);

  const fetchAcceptedPendingAppointmentsOfDoctor = () => {
    setAppointmentByDateFetching(true);
    axiosInstance
      .post(fetchAcceptedPendingAppointmentsOfDoctorByDate, {
        doctor_id: userId,
        date: `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`,
      })
      .then((res) => {
        // filter only accepted ones
        setBookedAppointments(res.data?.data?.filter((it) => it.status == 1));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setAppointmentByDateFetching(false);
      });
  };

  const fetchDoctorDetail = () => {
    setLoading(true);
    axiosInstance
      .post(fetchDoctorProfile, { doctor_id: userId })
      .then((res) => {
        setDoctorDetail(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDateSelect = (date) => {
    setSelectedDate(date);
  };

  return (
    <Wrapper>
      <Section topMd={200} topLg={10} topXl={110} bottomMd={200} bottomLg={150} bottomXl={110}>
        {loading && !doctorDetail && <FullPageLoader />}
        {!loading && doctorDetail && (
          <div className="content-wrapper">
            <div className="row">
              <div className="col-md-6">
                <CommonMobileHeader title="Dashboard" />
                <DoctorDetailHeader image={doctorDetail.image} degrees={doctorDetail?.degrees} designation={doctorDetail?.designation} name={doctorDetail?.name} />
              </div>
              <div className="col-md-6 g-2 px-3">
                <BookingDateSelector dates={bookingDates} selectedDate={selectedDate} onDateClick={onDateSelect} />
                <input type="text" className="rounded-pill px-3 w-100 py-2 border bg-grey2 text-gray1 fs-md text-black" placeholder="Search..." />
                {appointmentDataFetching && (
                  <div className="mt-3">
                    <Spinner />
                  </div>
                )}
                {!appointmentDataFetching && bookedAppointments?.length === 0 && <ListEmptyText text={"No appoinments available."} />}
                {!appointmentDataFetching && bookedAppointments?.length > 0 && (
                  <div className=" mx-2">
                    {bookedAppointments.map((bookedAppointment) => (
                      <Link to={`/appointment-details/${bookedAppointment.id}`} key={bookedAppointment.id} className="d-flex align-items-center bg-grey2 mb-2 mt-1 gap-2">
                        <div className="col-md-2 rounded m-1 position-relative">
                          {bookedAppointment.user?.profile_image == null ? (
                            <div className="d-flex justify-content-center align-items-center bg-info-surface" style={{ borderRadius: 8, height: 70, width: 70 }}>
                              <span className="text-sprimary mx-auto" style={{ fontSize: 40 }}>
                                {bookedAppointment?.user?.fullname?.charAt(0)}
                              </span>
                            </div>
                          ) : (
                            <img className="" src={`${imageBaseUrl}${bookedAppointment?.user?.profile_image}`} alt="" style={{ objectFit: "cover", borderRadius: 14, height: 70, width: 70 }} />
                          )}
                          <div className="position-absolute bookingTime">
                            <span>{format4DigitTimeToAmOrPM(bookedAppointment?.time)}</span>
                          </div>
                        </div>
                        <div className="col-md-6 mt-2">
                          <strong className="text-dark">{bookedAppointment?.user.fullname}</strong>
                          <p className="text-grey1 fs-md">{`${getAgeFromGivenDate(bookedAppointment?.user?.dob)} Years: ${bookedAppointment?.user.gender == 1 ? "Male" : "Female"}`}</p>
                        </div>
                        <Link to={`tel:+${bookedAppointment.user?.country_code}${bookedAppointment?.user?.phone_number?.split(" ")[0]}`} className="CallNowBtn">
                          Call Now
                        </Link>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Section>
    </Wrapper>
  );
}
const Wrapper = styled.div`
  .bookingTime {
    bottom: 0;
    background: #4d4d4d70;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    color: #fff;
    font-size: 0.8rem;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .CallNowBtn {
    background-color: #ebebeb;
    padding: 6px 16px;
    border-radius: 30px;
    align-self: center;
    margin-left: auto;
    margin-right: 0.5rem;
    font-weight: 500;
    font-size: 0.95rem;
    color: ${tuftsBlue};
    transition: all ease-in-out 0.3s;
    &:hover {
      background-color: #d6d6d6;
    }
  }
`;
